import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ISearchValue, ITableContext, TableColumnDefs, TablePaginationRef, TableProps } from '../table.model'
import { AgGridReact } from '@ag-grid-community/react'
import {
  defaultActionColId,
  defaultActionColumnMaxWidth,
  defaultColumnDefs,
  defaultOptions,
  defaultPageSize,
  defaultPagination,
  defaultSearch,
  defaultSearchData,
} from '../table.constants'
import { useForm, useWatch } from 'react-hook-form'
import { uniqBy } from 'lodash'
import { IGetUserListPayload } from 'src/app/shared/models/user.model'
import { ColumnState, SortChangedEvent } from '@ag-grid-community/core'
import { colorBgBrandless, colorText } from 'src/app/themes/primaryThemeDefault'
import { useSelector } from 'react-redux'
import { getCriticalConfig, getDeferredConfig } from 'src/app/store/masterSetup'
import TableActionColumn from './TableActionColumn'

export const paginationRef = React.createRef<TablePaginationRef>()
export const tableRef = React.createRef<AgGridReact<any>>()

const TableContext = React.createContext<ITableContext>({
  paginationRef,
  tableRef,
  columnDefs: [],
  columnOptions: [],
} as any)

export const useTableContext = () => useContext(TableContext)

const TableProvider: React.FC<PropsWithChildren<TableProps>> = (props) => {
  const {
    children,
    columnDefs,
    displayActionColumn = true,
    isUpdatingSetting,
    isGettingList,
    isGettingSetting,
    total = 0,
    rowData,
    tableHeaderBackgroundColor = colorBgBrandless,
    tableHeaderFontColor = colorText,
    disableTableControl,
    autoSort = true,
    handleSearch,
    handleChooseColumn,
    onSortChanged,
    suppressNoRowsOverlay = true,
    isSearchDropdown = false,
    ...etc
  } = props

  const paginationRef = useRef<TablePaginationRef>(null)
  const loading = useMemo(
    () => isUpdatingSetting || isGettingSetting || isGettingList,
    [isGettingList, isGettingSetting, isUpdatingSetting],
  )

  const criticalConfig = useSelector(getCriticalConfig)
  const majorConfig = useSelector(getDeferredConfig)

  const filterOutActionColumn = useCallback(
    (arr: Array<TableColumnDefs | ColumnState>): Array<TableColumnDefs | ColumnState> => {
      return arr?.filter((item) => !item?.hide && ![defaultActionColId].includes(item?.colId))
    },
    [],
  )

  const defs = useMemo<Array<TableColumnDefs>>(() => {
    const cols = uniqBy(
      columnDefs?.map((item, index) => {
        const data: TableColumnDefs = {
          ...item,
          ...defaultColumnDefs,
          sortIndex: autoSort ? index : item.sortIndex,
          sort: autoSort
            ? filterOutActionColumn(columnDefs).findIndex((_i) => _i.colId === item.colId) === 0
              ? 'asc'
              : null
            : columnDefs.findIndex((_i) => _i.sortIndex === 0) === index
            ? 'asc'
            : null,
          sortable: item.sortable ?? item.colId !== defaultActionColId,
          flex: item.flex ?? 1,
        }
        return data
      }),
      'colId',
    )

    return displayActionColumn
      ? [
          {
            colId: defaultActionColId,
            headerName: '',
            field: '',
            hide: false,
            defaultSelect: true,
            pinned: 'left',
            minWidth: defaultActionColumnMaxWidth,
            maxWidth: defaultActionColumnMaxWidth,
            cellRenderer: (props: any) => {
              const { isHideMenuAction } = props.data
              return isHideMenuAction ? '' : <TableActionColumn data={props.data} />
            },
          },
          ...cols,
        ]
      : cols
  }, [autoSort, columnDefs, displayActionColumn, filterOutActionColumn])

  const getTableState = useCallback(() => {
    return tableRef.current?.api?.getColumnState() ?? []
  }, [])

  // table search state
  const [searchOpen, setSearchOpen] = useState<boolean>(false)
  const { control, handleSubmit, reset, resetField } = useForm({
    defaultValues: defaultSearch,
  })
  const searchColumnValue = useWatch({
    control,
    name: 'searchColumns',
  })
  const searchTextvalue = useWatch({
    control,
    name: 'searchTerm',
  })

  //table search data state
  const [tableSearchData, setTableSearchData] = useState<Partial<IGetUserListPayload>>(defaultSearchData)

  // table dropdown states
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
  const [chosenColumns, setChosenColumn] = useState<Array<string>>([]) // state for displaying checkbox
  const [isSearch, setIsSearch] = useState<boolean>(false)

  // common functions for searching
  const getChosenColumns = useCallback(
    (values: { column: string; text: string }) => {
      return values.column !== defaultOptions[0].value
        ? [values.column]
        : filterOutActionColumn(getTableState()).map((item) => item.colId)
    },
    [filterOutActionColumn, getTableState],
  )

  // table pagination states
  const recordsPerPage = useMemo(
    () => majorConfig?.systemSettings.recordsPerPage ?? defaultPageSize,
    [majorConfig?.systemSettings.recordsPerPage],
  )
  const [currentPage, setCurrentPage] = useState<number>(defaultPagination)
  const [pageSize, setPageSize] = useState<number>(recordsPerPage)
  const totalRows = useMemo(() => (rowData && rowData?.length === 0 ? 0 : total), [rowData, total])

  const handleSearchLogic = useCallback(
    async (value: Partial<IGetUserListPayload>) => {
      console.log('HANDLE SEARCH LOGIC')
      const state = filterOutActionColumn(getTableState()) ?? []
      const sortColumns = state.filter((item) => item.sort)
      const chosenColumns = getChosenColumns({
        column: searchColumnValue,
        text: searchTextvalue,
      })
      const searchData: Partial<IGetUserListPayload> = {
        pageIndex: currentPage,
        pageSize: pageSize,
        // sortColumns: sortColumns.length > 0 ? sortColumns.map((item) => item.colId) : state.map((item) => item.colId),
        // sortDirection: sortColumns.length > 0 ? (sortColumns?.[0].sort as any) : '',
        searchColumns: chosenColumns,
        searchTerm: searchTextvalue,
        ...value,
      }

      if (sortColumns.length > 0) {
        searchData.sortColumns = sortColumns.map((item) => item.colId)
        searchData.isDesc = (sortColumns?.[0].sort as any) === 'desc'
      }

      await handleSearch(searchData)
      setTableSearchData(searchData)
      tableRef.current?.api.clearFocusedCell()
      tableRef.current?.api.clearRangeSelection()
    },
    [
      currentPage,
      filterOutActionColumn,
      getChosenColumns,
      getTableState,
      handleSearch,
      pageSize,
      searchColumnValue,
      searchTextvalue,
    ],
  )

  // table search methods
  const onSearch = useCallback(
    async (values: ISearchValue) => {
      setIsSearch(true)
      setCurrentPage(1)
      await handleSearchLogic({
        searchTerm: values.searchTerm,
        pageIndex: 1,
      })
    },
    [handleSearchLogic],
  )

  // table dropdown methods
  const onApplyColumn = useCallback(async () => {
    console.log('ON APPLY COLUMN')
    const state = getTableState()?.map((item) => ({
      ...item,
      hide: [defaultActionColId].includes(item.colId) ? false : !chosenColumns.includes(item.colId),
    }))
    tableRef.current?.api?.applyColumnState({
      state,
    })
    await handleChooseColumn(
      tableRef.current?.api.getColumnDefs()?.filter((item: any) => item.colId !== defaultActionColId) as any,
    )
    setDropdownOpen(false)
  }, [chosenColumns, getTableState, handleChooseColumn])

  const onCancel = useCallback(() => {
    setChosenColumn(filterOutActionColumn(getTableState()).map((item) => item.colId))
    setDropdownOpen(false)
  }, [filterOutActionColumn, getTableState])

  const onToggleDropdown = useCallback(() => {
    const newOpen = !dropdownOpen
    // save state when open for canceling/closing dropdown
    if (!newOpen) {
      setChosenColumn(filterOutActionColumn(getTableState()).map((item) => item.colId))
    }
    setDropdownOpen(newOpen)
  }, [dropdownOpen, filterOutActionColumn, getTableState])

  // table pagination methods
  const setCurrent = useCallback(
    async (page: number, pageSize: number) => {
      setCurrentPage(page)
      setPageSize(pageSize)
      // AG Grid pagination first page is zero
      // AntDesign pagination first page is one
      tableRef.current?.api.paginationGoToPage(page - defaultPagination)
      tableRef.current?.api.updateGridOptions({
        paginationPageSize: pageSize,
      })
      await handleSearchLogic({
        pageIndex: page,
        pageSize,
      })
    },
    [handleSearchLogic],
  )

  const onJumpToFirst = useCallback(async () => {
    await setCurrent(defaultPagination, pageSize)
  }, [pageSize, setCurrent])

  const onJumpToLast = useCallback(async () => {
    const lastPage = Math.ceil(totalRows / pageSize)
    await setCurrent(lastPage, pageSize)
  }, [pageSize, setCurrent, totalRows])

  const onPaginationChange = useCallback(
    async (page: number, pageSize: number) => {
      await setCurrent(page, pageSize)
    },
    [setCurrent],
  )

  const onPageSizeChange = useCallback(
    async (size: number) => {
      await setCurrent(defaultPagination, size)
    },
    [setCurrent],
  )

  // reset button methods
  const onResetView = useCallback(async () => {
    console.log('ON RESET VIEW')
    const newDefs: Array<any> =
      tableRef.current?.api.getColumnDefs()?.map((item: any, index) => ({
        ...item,
        ...defaultColumnDefs,
        hide: !(defs.find((col) => col.colId === item.colId)?.defaultSelect || false),
        sort: index === 1 ? 'asc' : null, // first column index (0) is action column
      })) ?? []
    await handleChooseColumn(newDefs)

    await handleSearchLogic({
      pageIndex: defaultPagination,
      pageSize: recordsPerPage,
      searchColumns: [],
      searchTerm: defaultSearch.searchTerm,
    })

    // reset search field
    reset()
    setSearchOpen(false)

    // reset dropdown
    setDropdownOpen(false)
    setChosenColumn(filterOutActionColumn(newDefs).map((item) => item.colId))

    // reset pagination
    setPageSize(recordsPerPage)
    setCurrentPage(defaultPagination)
    paginationRef.current?.onReset?.()
    tableRef.current?.api.clearFocusedCell()
    tableRef.current?.api.clearRangeSelection()
    tableRef.current?.api.paginationGoToFirstPage()
    tableRef.current?.api.updateGridOptions({
      paginationPageSize: recordsPerPage,
    })
  }, [defs, recordsPerPage, filterOutActionColumn, handleChooseColumn, handleSearchLogic, reset])

  // sort change
  const _onSortChanged = useCallback(
    async (event: SortChangedEvent<any, any>) => {
      console.log('ON SORT CHANGE')
      await handleSearchLogic?.({})
      onSortChanged?.(event)
    },
    [handleSearchLogic, onSortChanged],
  )

  // width
  const onColumnResized = useCallback(() => {
    const state = getTableState().map((item) => ({ ...item, maxWidth: undefined })) as any
    const updatedDefs = defs.map((item, index) => ({ ...item, ...state[index] }))
    tableRef.current?.api.updateGridOptions({
      columnDefs: updatedDefs,
    })
  }, [defs, getTableState])

  useEffect(() => {
    if (isSearchDropdown) {
      resetField('searchTerm')
    }
  }, [isSearchDropdown])

  useEffect(() => {
    if (suppressNoRowsOverlay && (!rowData?.length || rowData.length === 0)) {
      tableRef.current?.api?.setGridOption('suppressNoRowsOverlay', false)
      tableRef.current?.api?.showNoRowsOverlay()
      return
    }

    tableRef.current?.api?.setGridOption('suppressNoRowsOverlay', true)
    tableRef.current?.api?.hideOverlay()
  }, [rowData, suppressNoRowsOverlay])

  useEffect(() => {
    if (loading) {
      tableRef.current?.api?.showLoadingOverlay()
      return
    }

    const isDisplayResultNotFound = !tableRef.current?.api?.getGridOption('suppressNoRowsOverlay')
    if (!isDisplayResultNotFound) tableRef.current?.api?.hideOverlay()
  }, [loading])

  useEffect(() => {
    setChosenColumn(filterOutActionColumn(defs)?.map((item) => item.colId))
  }, [defs, filterOutActionColumn, setChosenColumn])

  useEffect(() => {
    if (disableTableControl) {
      const state = getTableState().map((item) => ({ ...item, sortable: false, resizable: false }))
      tableRef.current?.api?.applyColumnState({
        state,
      })
    }
  }, [disableTableControl, getTableState])

  return (
    <TableContext.Provider
      value={{
        defs,
        tableRef,
        paginationRef,
        columnDefs: defs,
        tableHeaderBackgroundColor:
          criticalConfig?.systemColor.tableHeaderBackgroundColor ?? tableHeaderBackgroundColor,
        tableHeaderFontColor: criticalConfig?.systemColor.tableHeaderFontColor ?? tableHeaderFontColor,
        tableSearchData,
        searchOpen,
        control,
        displayActionColumn,
        total: totalRows,
        rowData,
        chosenColumns,
        isUpdatingSetting,
        isGettingList,
        isGettingSetting,
        currentPage,
        pageSize,
        dropdownOpen,
        disableTableControl,
        searchTerm: searchTextvalue,
        setChosenColumn,
        setSearchOpen,
        handleSearch,
        handleChooseColumn,
        handleSubmit,
        onColumnResized,
        onSortChanged: _onSortChanged,
        onResetView,
        onPageSizeChange,
        onPaginationChange,
        onJumpToFirst,
        onJumpToLast,
        onToggleDropdown,
        onCancel,
        onApplyColumn,
        onSearch,
        isSearch,
        suppressNoRowsOverlay,
        isSearchDropdown,
        ...etc,
      }}
    >
      {children}
    </TableContext.Provider>
  )
}

export default TableProvider
